/* eslint-disable */
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSunnyOutline } from 'react-icons/io5';
import { FaWind, FaCar, FaBolt, FaMicrochip, FaBitcoin } from 'react-icons/fa';
import { BsBatteryCharging, BsBattery, BsBatteryFull } from 'react-icons/bs';
import { useGetB2bPublicMinerPowerQuery, useGetB2bPublicRealtimePowerQuery } from '../../redux/api';
import { HOST, PORT } from '../../environment';
import { fetchOpenEmsActivePortsGreenEnergyPct, fetchOpenEmsFleetGreenEnergyPct } from '../../utils/openEmsEvPortBattery';
import { SVGLogo } from '../../components/svgComponents/SVGLogo';
import Modal from '../../components/modal/Modal';
import DayKwhChart from './DayKwhChart';
import { createPortStickerQrDataUrl } from '../startPage/portStickerQr';
import styles from './FlowGraph.module.css';

/** Battery icon with 2/3 fill (Bootstrap-style outline + proportional fill). */
function BatteryTwoThirds({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <path d="M0 6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1zm14 3a1.5 1.5 0 0 1-1.5 1.5v-3A1.5 1.5 0 0 1 16 8z" />
      <rect x="2" y="6" width="6.67" height="4" rx="0.5" />
    </svg>
  );
}

/** Number locale for flow graph (matches i18n language). */
function powerFormatLocale(lang) {
  const base = typeof lang === 'string' ? lang.split('-')[0] : '';
  if (base === 'en') return 'en-GB';
  if (base === 'ru') return 'ru-UA';
  return 'uk-UA';
}

/** Format power in kW or MW only (no W); units from i18n. */
function formatPowerParts(watts, t, language) {
  if (watts == null || !Number.isFinite(watts)) return { value: '—', unit: '' };
  const locale = powerFormatLocale(language);
  const nf = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (watts >= 1_000_000) {
    return { value: nf.format(watts / 1_000_000), unit: t('b2bFlowGraphUnitMw') };
  }
  return { value: nf.format(watts / 1000), unit: t('b2bFlowGraphUnitKw') };
}

function formatPower(watts, t, language) {
  const { value, unit } = formatPowerParts(watts, t, language);
  return unit ? `${value}\u00a0${unit}` : value;
}

function NodePowerValue({ watts, prefix, className, t, language }) {
  const { value, unit } = formatPowerParts(watts, t, language);
  return (
    <div className={className}>
      {prefix ? <span className={styles.nodeValuePrefix}>{prefix}</span> : null}
      <span className={styles.nodeValueAmount}>{value}</span>
      {unit ? <span className={styles.nodeValueUnit}>{unit}</span> : null}
    </div>
  );
}

function formatUsdt(value, language) {
  if (value == null || !Number.isFinite(value)) return null;
  const locale = powerFormatLocale(language);
  const nf = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `+${nf.format(Math.max(0, value))}`;
}

function formatTariffUahPerKwh(value, language, t) {
  if (value == null || !Number.isFinite(value)) return null;
  const locale = powerFormatLocale(language);
  const nf = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const tariffUnit = t('b2bMarketTariffUnitUahPerKwh');
  const tariffUnitWithSign = tariffUnit.includes('/')
    ? tariffUnit.replace(/^[^/]+/, '\u20b4').replace(/кВт·год|кВт·ч|kWh/gi, 'кВтг')
    : '\u20b4/кВтг';
  return `${nf.format(Math.max(0, value))} ${tariffUnitWithSign}`;
}

/** Slower travel along flow lines (SVG animateMotion). */
const FLOW_DOT_MOTION_DUR = '2.2s';

/** Binance Pool public statistics link for the ISIC miner node (220km account). */
const BINANCE_MINER_OBSERVER_URL =
  'https://pool.binance.com/en/statistics?urlParams=k0L2WD9yFZlqcgCbBtRfiu040xT4UPvxRgFKVq0hr4k08962';

/** Sirocco Energy suburban wind turbine (PDF on Google Drive). */
const WIND_NODE_DOC_URL = 'https://drive.google.com/file/d/102AcXuk6Cz4Zn7EMfHhPO2HnkMo7_2nc/view?usp=sharing';

/** Station list map (EV charging node on B2B flow graph). */
const EV_CHARGING_LIST_URL = 'https://220-km.com/list';
const GREEN_ENERGY_POLL_MS = 15_000;

/** Alumni Sans can draw '%' as wrong glyph; force a stack with a reliable U+0025. */
const FONT_STACK_PERCENT_SAFE =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

/** Open EMS power-flow UI (override via REACT_APP_OPEN_EMS_UI_URL for non-prod hosts). */
const OPEN_EMS_UI_URL =
  (typeof process !== 'undefined' &&
    process.env.REACT_APP_OPEN_EMS_UI_URL &&
    String(process.env.REACT_APP_OPEN_EMS_UI_URL).trim()) ||
  'https://220-km.com:9220/';

/** ESS vendor wordmarks (under public/b2b-ess-vendors); href = official product/brand pages. */
const ESS_VENDOR_LOGOS = [
  { file: 'deye.png', alt: 'Deye', href: 'https://www.deyeinverter.com/' },
  { file: 'huawei.png', alt: 'Huawei FusionSolar', href: 'https://solar.huawei.com/' },
  {
    file: 'biom.svg',
    alt: 'Biom',
    href: 'https://docs.google.com/spreadsheets/d/1t4eaa6mua3hA6qBZjNnbQgHQCgVul0MYfERBsoW8reo/edit?usp=sharing',
  },
  {
    file: 'ubetter.png',
    alt: 'UBETTER',
    href: 'https://www.ubetterenergy.com/',
    certificateFile: 'ubetter-official-service-certificate.png',
  },
  { file: 'kstar.svg', alt: 'Kstar', href: 'https://www.kstar.com.cn/en' },
  { file: 'invt.png', alt: 'INVT', href: 'https://www.invt.com/ru/hybrid-inverters' },
];

/** Certificate modal link: show registrable domain only (no "manufacturer website" copy). */
function certificateModalSiteDisplayName(href) {
  try {
    const { hostname } = new URL(href);
    return hostname.replace(/^www\./i, '') || hostname;
  } catch {
    return href;
  }
}

/** Dot reaches exact line end (no arrow markers now). */
const FLOW_DOT_PATH_END_INSET = 0;

/**
 * Point on segment (x1,y1)-(x2,y2) moved inward from (x2,y2) toward (x1,y1).
 */
function lineEndInset(x1, y1, x2, y2, inset) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const clamped = Math.min(inset, len * 0.42);
  const ux = dx / len;
  const uy = dy / len;
  return { x: x2 - ux * clamped, y: y2 - uy * clamped };
}

/** Line segment centered on the geometric midpoint between node and hub (stays in the gap). */
const LINE_EDGE_INSET = 44;

function lineCenteredBetween(nodeX, nodeY, hubX, hubY, inset = LINE_EDGE_INSET) {
  const dx = hubX - nodeX;
  const dy = hubY - nodeY;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const midX = (nodeX + hubX) / 2;
  const midY = (nodeY + hubY) / 2;
  const halfLen = Math.min(inset, len * 0.45) / 2;
  return {
    start: { x: midX - ux * halfLen, y: midY - uy * halfLen },
    end: { x: midX + ux * halfLen, y: midY + uy * halfLen },
  };
}

/** Shorter dash segment in the gap between two pixel anchor points (mobile grid layout). */
const MOBILE_FLOW_LINE_INSET_PX = 26;

function lineCenteredBetweenPx(nodeX, nodeY, hubX, hubY, inset = MOBILE_FLOW_LINE_INSET_PX) {
  return lineCenteredBetween(nodeX, nodeY, hubX, hubY, inset);
}

/**
 * Build dashed-line segments for the stacked mobile graph (measured vs container).
 * refs: { solar, grid, wind, hub, ess, miner, consumption } DOM elements.
 */
function measureMobileStackFlowGeometry(containerEl, refs) {
  if (!containerEl || !refs) return null;
  const cr = containerEl.getBoundingClientRect();
  const w = containerEl.offsetWidth;
  const h = containerEl.offsetHeight;
  if (w <= 0 || h <= 0) return null;
  const keys = ['solar', 'grid', 'wind', 'hub', 'ess', 'miner', 'consumption'];
  for (let i = 0; i < keys.length; i += 1) {
    const el = refs[keys[i]];
    if (!el || !(el instanceof Element)) return null;
  }

  const bb = el => el.getBoundingClientRect();
  const bottomMid = el => {
    const b = bb(el);
    return { x: b.left + b.width / 2 - cr.left, y: b.bottom - cr.top - 1 };
  };
  const topMid = el => {
    const b = bb(el);
    return { x: b.left + b.width / 2 - cr.left, y: b.top - cr.top + 1 };
  };

  const { solar, grid, wind, hub, ess, miner, consumption } = refs;
  const hubTop = topMid(hub);
  const hubBottom = bottomMid(hub);
  const inset = MOBILE_FLOW_LINE_INSET_PX;

  return {
    w,
    h,
    solar: lineCenteredBetweenPx(bottomMid(solar).x, bottomMid(solar).y, hubTop.x, hubTop.y, inset),
    gridBuy: lineCenteredBetweenPx(bottomMid(grid).x, bottomMid(grid).y, hubTop.x, hubTop.y, inset),
    gridSell: lineCenteredBetweenPx(hubTop.x, hubTop.y, bottomMid(grid).x, bottomMid(grid).y, inset),
    wind: lineCenteredBetweenPx(bottomMid(wind).x, bottomMid(wind).y, hubTop.x, hubTop.y, inset),
    essDischarge: lineCenteredBetweenPx(topMid(ess).x, topMid(ess).y, hubBottom.x, hubBottom.y, inset),
    essCharge: lineCenteredBetweenPx(hubBottom.x, hubBottom.y, topMid(ess).x, topMid(ess).y, inset),
    miner: lineCenteredBetweenPx(hubBottom.x, hubBottom.y, topMid(miner).x, topMid(miner).y, inset),
    consumption: lineCenteredBetweenPx(hubBottom.x, hubBottom.y, topMid(consumption).x, topMid(consumption).y, inset),
  };
}

/** Motion path from start to near-end (clears marker); eases via calcMode spline on animateMotion. */
function flowMotionPath(x1, y1, x2, y2, insetEnd = FLOW_DOT_PATH_END_INSET) {
  const { x, y } = lineEndInset(x1, y1, x2, y2, insetEnd);
  return `M ${x1} ${y1} L ${x} ${y}`;
}

/**
 * Intersection of ray from (cx,cy) toward (toX,toY) with rectangle centered at (cx,cy).
 * Returns the point on the rect edge that the ray hits when leaving the rect.
 */
function rectEdgeToward(cx, cy, halfW, halfH, toX, toY) {
  const dx = toX - cx;
  const dy = toY - cy;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const tX = Math.abs(ux) > 1e-9 ? (ux > 0 ? halfW : -halfW) / ux : Infinity;
  const tY = Math.abs(uy) > 1e-9 ? (uy > 0 ? halfH : -halfH) / uy : Infinity;
  const t = Math.min(tX, tY);
  return { x: cx + ux * t, y: cy + uy * t };
}

/**
 * Wall-clock time in Europe/Kiev (fractional hour) for demo solar / ESS logic.
 */
function getKyivWallClock(date) {
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Kiev',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const map = {};
  for (const p of parts) {
    if (p.type !== 'literal') {
      map[p.type] = p.value;
    }
  }
  const month = Number(map.month) - 1;
  const hour = Number(map.hour);
  const minute = Number(map.minute);
  const second = Number(map.second);
  const hourFloat = hour + minute / 60 + second / 3600;
  return { month, hour, hourFloat };
}

/**
 * Approximate Kyiv PV-producing window by month (fractional local hours).
 * Outside [start, end) solar is forced to 0 (after sunset / before sunrise).
 */
const KYIV_PV_START_HOUR = [8, 7.5, 7, 6, 5.5, 5, 5, 5.5, 6, 6.5, 7.5, 8];
const KYIV_PV_END_HOUR = [16, 17, 18.5, 19.5, 20.5, 21.5, 21, 20, 18.5, 17, 16, 15.5];

function isKyivPvHours(month, hourFloat) {
  const start = KYIV_PV_START_HOUR[month] ?? 7;
  const end = KYIV_PV_END_HOUR[month] ?? 18;
  return hourFloat >= start && hourFloat < end;
}

/**
 * Simulate source power breakdown based on time and ESS logic.
 * ESS: 18-21 from ESS (discharging), 11-16 and PV hours to ESS (charging).
 * All clock logic uses Europe/Kiev. Solar is 0 outside approximate sunrise–sunset window.
 */
/**
 * @param {number | null | undefined} liveMinerW - when set (W), replaces demo miner from Binance Pool API
 */
function useSimulatedSources(consumptionMw, liveMinerW) {
  const [minuteTick, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuteTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    const now = new Date();
    const { month, hour, hourFloat } = getKyivWallClock(now);
    const consumptionW = consumptionMw != null && Number.isFinite(consumptionMw) ? consumptionMw * 1e6 : 0;

    const useLiveMiner = liveMinerW != null && Number.isFinite(liveMinerW) && liveMinerW >= 0 ? liveMinerW : null;

    const isPvHours = isKyivPvHours(month, hourFloat);

    // ESS discharging: 18-21 Kyiv
    const essDischarging = hour >= 18 && hour <= 21;
    // ESS charging: 11-16 Kyiv and PV hours
    const essCharging = hour >= 11 && hour <= 16 && isPvHours;

    let solarW = 0;
    let windW = 0;
    let essW = 0;
    let gridW = 0;

    const MAX_ESS_POWER_W = 100000; // 100 kW
    const MAX_SOLAR_POWER_W = 20000; // 20 kW
    const MIN_SOLAR_TO_ESS_PCT = 0.07; // always charge min 7% of solar to ESS when sunny
    const MIN_SOLAR_TO_MINER_PCT = 0.04; // ISIC miner: min 4% of solar, solar only
    const NIGHT_GRID_TO_ESS_W = 200000; // 200 kW from grid to ESS at night 1-3 am
    const MIN_GRID_TO_EV_PCT = 0.3; // grid covers at least 30% of EV charging load

    const nightGridCharge = hour >= 1 && hour < 3; // Kyiv night
    let minerW = 0;

    if (nightGridCharge) {
      // 1-3 am: charge ESS from grid (200 kW)
      essW = -NIGHT_GRID_TO_ESS_W;
      gridW = NIGHT_GRID_TO_ESS_W + Math.max(0, consumptionW);
    } else if (consumptionW > 0 || isPvHours) {
      // Solar curve: peak ~13:00 Kyiv, only during PV window (zero after sunset)
      if (isPvHours) {
        const solarPeak = Math.min((consumptionW || 50000) * 1.3, MAX_SOLAR_POWER_W);
        const h = hourFloat;
        const noon = 13;
        const spread = 5;
        solarW = Math.min(MAX_SOLAR_POWER_W, Math.max(0, solarPeak * Math.exp(-Math.pow((h - noon) / spread, 2))));
        minerW = solarW * MIN_SOLAR_TO_MINER_PCT; // solar only, min 4%
      }
      if (useLiveMiner != null) {
        minerW = useLiveMiner;
      }

      // EV consumption from API; solar after miner = solarW - minerW
      const solarAfterMiner = solarW - minerW;

      if (consumptionW > 0) {
        if (essDischarging) {
          const essSupply = Math.min(consumptionW * 0.3, MAX_ESS_POWER_W);
          essW = essSupply;
          gridW = Math.max(0, consumptionW - solarAfterMiner - essSupply);
        } else if (isPvHours && solarW > 0) {
          const minEssCharge = Math.min(solarW * MIN_SOLAR_TO_ESS_PCT, MAX_ESS_POWER_W);
          const solarForEvAndEss = solarAfterMiner - minEssCharge;

          if (solarForEvAndEss >= consumptionW) {
            const excess = solarForEvAndEss - consumptionW;
            const additionalToEss = Math.min(excess, MAX_ESS_POWER_W - minEssCharge);
            essW = -(minEssCharge + additionalToEss);
            gridW = -Math.max(0, excess - additionalToEss);
          } else {
            essW = -minEssCharge;
            gridW = consumptionW - solarForEvAndEss;
          }
        } else {
          gridW = consumptionW - solarAfterMiner;
        }
      }

      // Enforce grid covers at least 30% of EV load; reduce solar to keep balance
      if (consumptionW > 0) {
        const minGridToEvW = consumptionW * MIN_GRID_TO_EV_PCT;
        if (gridW < minGridToEvW) {
          const diff = minGridToEvW - gridW;
          gridW = minGridToEvW;
          solarW = Math.max(0, solarW - diff);
          if (useLiveMiner == null) {
            minerW = solarW * MIN_SOLAR_TO_MINER_PCT; // recalc demo miner from reduced solar
          }
        }
      }
    }

    if (useLiveMiner != null) {
      minerW = useLiveMiner;
    }

    return {
      solarW,
      windW,
      gridW,
      essW,
      minerW,
      essCharging: essW < 0,
      essDischarging: essW > 0,
      consumptionW,
    };
  }, [consumptionMw, liveMinerW, minuteTick]);
}

function FlowGraph({ station, wideView = false, inPageSection = false }) {
  const { t, i18n } = useTranslation();
  const flowSvgUid = useId().replace(/:/g, '');
  const { data: realtimePower, isLoading } = useGetB2bPublicRealtimePowerQuery(
    { station: station || undefined },
    { pollingInterval: 5000 }
  );

  const consumptionMw = realtimePower?.powerMw ?? 0;
  const { data: minerSnap } = useGetB2bPublicMinerPowerQuery(undefined, { pollingInterval: 30_000 });
  const liveMinerW =
    minerSnap?.configured && minerSnap.powerW != null && Number.isFinite(minerSnap.powerW)
      ? Math.max(0, minerSnap.powerW)
      : null;
  const minedUsdtToday = formatUsdt(minerSnap?.minedUsdtToday, i18n.language);
  const minerTariffUahPerKwh = formatTariffUahPerKwh(minerSnap?.tariffUahPerKwh, i18n.language, t);
  const { solarW, windW, gridW, essW, minerW, essCharging, essDischarging, consumptionW } = useSimulatedSources(
    consumptionMw,
    liveMinerW
  );

  const [greenEnergyPct, setGreenEnergyPct] = useState(null);
  const [greenEnergyPctLoading, setGreenEnergyPctLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadGreenEnergyPct = async () => {
      if (consumptionW <= 0) {
        if (!cancelled) {
          setGreenEnergyPct(null);
          setGreenEnergyPctLoading(false);
        }
        return;
      }

      const apiOrigin = `${HOST}:${PORT}`;
      let value = await fetchOpenEmsActivePortsGreenEnergyPct(apiOrigin, consumptionW);
      if (value == null) {
        value = await fetchOpenEmsFleetGreenEnergyPct();
      }
      if (!cancelled) {
        setGreenEnergyPct(value);
        setGreenEnergyPctLoading(false);
      }
    };

    setGreenEnergyPctLoading(true);
    void loadGreenEnergyPct();
    const id = window.setInterval(() => void loadGreenEnergyPct(), GREEN_ENERGY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [consumptionW]);

  const gridSelling = gridW < 0;

  const hasFlow = consumptionW > 0 || minerW > 0 || essW !== 0 || gridW !== 0;

  const containerRef = useRef(null);
  const flowNodeRefs = useRef({});
  const setFlowNodeRef = key => el => {
    if (el) flowNodeRefs.current[key] = el;
    else delete flowNodeRefs.current[key];
  };

  const [containerSize, setContainerSize] = useState({ w: 400, h: 400 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w > 0 && h > 0) setContainerSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Embedded (non-kiosk) graph: CSS grid stack + measured flow SVG on all viewport widths. */
  const useEmbeddedStackLayout = !wideView;

  const [mobileFlowGeom, setMobileFlowGeom] = useState(null);
  useLayoutEffect(() => {
    if (wideView) {
      setMobileFlowGeom(null);
      return undefined;
    }
    const measure = () => {
      const c = containerRef.current;
      if (!c) return;
      const g = measureMobileStackFlowGeometry(c, flowNodeRefs.current);
      setMobileFlowGeom(g);
    };
    measure();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure);
    });
    const el = containerRef.current;
    if (el) ro.observe(el);
    window.addEventListener('resize', measure);
    const raf = requestAnimationFrame(measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      cancelAnimationFrame(raf);
    };
  }, [
    wideView,
    containerSize.w,
    containerSize.h,
    hasFlow,
    gridSelling,
    essCharging,
    consumptionW,
    essW,
    minerW,
    gridW,
    solarW,
    windW,
    isLoading,
    minerSnap?.configured,
    minedUsdtToday,
    minerTariffUahPerKwh,
  ]);

  // SVG path coordinates (viewBox 0 0 400 400). Node centers match CSS layout.
  const cx = 200;
  const cy = 200;
  const toViewBox = px => (400 * px) / Math.max(containerSize.w, 1);
  /** Wide kiosk: keep in sync with `--flow-wide-node-anchor-px` in FlowGraph.module.css */
  const nwWide = toViewBox(34);
  /** Default graph: match --flow-node-inset-x / --flow-node-inset-y-* in FlowGraph.module.css */
  const insetXPx = 4;
  const insetYTopPx = -10;
  const insetYBottomPx = -10;
  const estNodeHalfW = 45;
  const estNodeHalfH = 50;
  const bottomRowShiftPx = 7;
  /** Wide horizontal cards: half-size in px for SVG edge math (match CSS min-width / height) */
  const nodeRectHalfWPx = wideView ? 108 : 45;
  const nodeRectHalfHPx = wideView ? 40 : 45;
  const nodeHalfW = toViewBox(nodeRectHalfWPx);
  const nodeHalfH = toViewBox(nodeRectHalfHPx);

  let solar;
  let grid;
  let wind;
  let ess;
  let miner;
  let consumption;
  if (wideView) {
    solar = { x: nwWide, y: nwWide };
    grid = { x: nwWide, y: cy };
    wind = { x: nwWide, y: 400 - nwWide };
    ess = { x: 400 - nwWide, y: nwWide };
    miner = { x: 400 - nwWide, y: cy };
    consumption = { x: 400 - nwWide, y: 400 - nwWide };
  } else {
    const xL = toViewBox(insetXPx + estNodeHalfW);
    const xR = 400 - xL;
    const yT = toViewBox(insetYTopPx + estNodeHalfH);
    const yB = 400 - toViewBox(insetYBottomPx + estNodeHalfH - bottomRowShiftPx);
    solar = { x: xL, y: yT };
    grid = { x: cx, y: yT };
    wind = { x: xR, y: yT };
    ess = { x: xL, y: yB };
    miner = { x: cx, y: yB };
    consumption = { x: xR, y: yB };
  }

  // Hub box edge in viewBox units (keep in sync with .flowGraphWide .hub size)
  const hubHalfW = wideView ? 59 : 52;
  const hubHalfH = wideView ? 47 : 41;
  const nodeEdgeTowardHub = (nodeX, nodeY) => rectEdgeToward(nodeX, nodeY, nodeHalfW, nodeHalfH, cx, cy);

  const kioskIconSize = wideView ? 43 : 28;
  const flowDotR = wideView ? 5.8 : 5;
  const hubEdgeToward = (fromX, fromY) => {
    const dx = fromX - cx;
    const dy = fromY - cy;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const t = Math.min(hubHalfW / Math.abs(ux || 0.001), hubHalfH / Math.abs(uy || 0.001));
    return { x: cx + ux * t, y: cy + uy * t };
  };
  const solarEdge = hubEdgeToward(solar.x, solar.y);
  const gridEdge = hubEdgeToward(grid.x, grid.y);
  const windEdge = hubEdgeToward(wind.x, wind.y);
  const essEdge = hubEdgeToward(ess.x, ess.y);
  const minerEdge = hubEdgeToward(miner.x, miner.y);
  const consumptionEdge = hubEdgeToward(consumption.x, consumption.y);
  const solarNodeEdge = nodeEdgeTowardHub(solar.x, solar.y);
  const gridNodeEdge = nodeEdgeTowardHub(grid.x, grid.y);
  const windNodeEdge = nodeEdgeTowardHub(wind.x, wind.y);
  const essNodeEdge = nodeEdgeTowardHub(ess.x, ess.y);
  const minerNodeEdge = nodeEdgeTowardHub(miner.x, miner.y);
  const consumptionNodeEdge = nodeEdgeTowardHub(consumption.x, consumption.y);

  const solarLine = lineCenteredBetween(solar.x, solar.y, cx, cy);
  const gridLineRaw = lineCenteredBetween(grid.x, grid.y, cx, cy);
  const gridLineSellingRaw = lineCenteredBetween(cx, cy, grid.x, grid.y);
  const gridLine = wideView
    ? { start: { x: gridLineRaw.start.x, y: cy }, end: { x: gridLineRaw.end.x, y: cy } }
    : { start: { x: cx, y: gridLineRaw.start.y }, end: { x: cx, y: gridLineRaw.end.y } };
  const gridLineSelling = wideView
    ? { start: { x: gridLineSellingRaw.start.x, y: cy }, end: { x: gridLineSellingRaw.end.x, y: cy } }
    : { start: { x: cx, y: gridLineSellingRaw.start.y }, end: { x: cx, y: gridLineSellingRaw.end.y } };
  const windLine = lineCenteredBetween(wind.x, wind.y, cx, cy);
  const essLine = lineCenteredBetween(ess.x, ess.y, cx, cy);
  const essLineCharging = lineCenteredBetween(cx, cy, ess.x, ess.y);
  const minerLine = lineCenteredBetween(cx, cy, miner.x, miner.y);
  const consumptionLine = lineCenteredBetween(cx, cy, consumption.x, consumption.y);

  const rootClass = [styles.flowGraph, wideView && styles.flowGraphWide, inPageSection && styles.flowGraphInPageSection]
    .filter(Boolean)
    .join(' ');
  const graphBoxClass = wideView ? `${styles.graphContainer} ${styles.graphContainerWide}` : styles.graphContainer;

  const flowDotMobileR = 4.5;
  const mg = mobileFlowGeom;

  const [marketChartOpen, setMarketChartOpen] = useState(false);
  const marketChartTitleId = useId().replace(/:/g, '');
  const [essVendorsOpen, setEssVendorsOpen] = useState(false);
  const essVendorsTitleId = useId().replace(/:/g, '');
  const [essVendorShare, setEssVendorShare] = useState(null);
  const essVendorShareTitleId = useId().replace(/:/g, '');
  const [essVendorShareQrUrl, setEssVendorShareQrUrl] = useState('');
  const [essVendorShareCopied, setEssVendorShareCopied] = useState(false);
  const [essVendorCertificate, setEssVendorCertificate] = useState(null);
  const essVendorCertificateTitleId = useId().replace(/:/g, '');
  const [minerShareOpen, setMinerShareOpen] = useState(false);
  const minerShareTitleId = useId().replace(/:/g, '');
  const [minerShareQrUrl, setMinerShareQrUrl] = useState('');
  const [minerShareCopied, setMinerShareCopied] = useState(false);
  const [windShareOpen, setWindShareOpen] = useState(false);
  const windShareTitleId = useId().replace(/:/g, '');
  const [windShareQrUrl, setWindShareQrUrl] = useState('');
  const [windShareCopied, setWindShareCopied] = useState(false);
  const publicUrl = process.env.PUBLIC_URL || '';

  useEffect(() => {
    if (!essVendorsOpen) {
      setEssVendorCertificate(null);
      setEssVendorShare(null);
    }
  }, [essVendorsOpen]);

  useEffect(() => {
    const shareHref = essVendorShare?.href;
    if (!shareHref) {
      setEssVendorShareQrUrl('');
      return undefined;
    }

    let active = true;
    try {
      const url = createPortStickerQrDataUrl(shareHref, {
        size: 280,
        margin: 2,
        color: '#111111',
        background: '#ffffff',
        finderRingColor: '#ffffff',
      });
      if (active) setEssVendorShareQrUrl(url);
    } catch {
      if (active) setEssVendorShareQrUrl('');
    }

    return () => {
      active = false;
    };
  }, [essVendorShare]);

  const copyEssVendorShareLink = async () => {
    const shareHref = essVendorShare?.href;
    if (!shareHref) return;
    try {
      await navigator.clipboard.writeText(shareHref);
      setEssVendorShareCopied(true);
      setTimeout(() => setEssVendorShareCopied(false), 2000);
    } catch {
      setEssVendorShareCopied(false);
    }
  };

  useEffect(() => {
    if (!minerShareOpen) {
      setMinerShareQrUrl('');
      return undefined;
    }

    let active = true;
    try {
      const url = createPortStickerQrDataUrl(BINANCE_MINER_OBSERVER_URL, {
        size: 280,
        margin: 2,
        color: '#111111',
        background: '#ffffff',
        finderRingColor: '#ffffff',
      });
      if (active) setMinerShareQrUrl(url);
    } catch {
      if (active) setMinerShareQrUrl('');
    }

    return () => {
      active = false;
    };
  }, [minerShareOpen]);

  const copyMinerShareLink = async () => {
    try {
      await navigator.clipboard.writeText(BINANCE_MINER_OBSERVER_URL);
      setMinerShareCopied(true);
      setTimeout(() => setMinerShareCopied(false), 2000);
    } catch {
      setMinerShareCopied(false);
    }
  };

  useEffect(() => {
    if (!windShareOpen) {
      setWindShareQrUrl('');
      return undefined;
    }

    let active = true;
    try {
      const url = createPortStickerQrDataUrl(WIND_NODE_DOC_URL, {
        size: 280,
        margin: 2,
        color: '#111111',
        background: '#ffffff',
        finderRingColor: '#ffffff',
      });
      if (active) setWindShareQrUrl(url);
    } catch {
      if (active) setWindShareQrUrl('');
    }

    return () => {
      active = false;
    };
  }, [windShareOpen]);

  const copyWindShareLink = async () => {
    try {
      await navigator.clipboard.writeText(WIND_NODE_DOC_URL);
      setWindShareCopied(true);
      setTimeout(() => setWindShareCopied(false), 2000);
    } catch {
      setWindShareCopied(false);
    }
  };

  return (
    <div className={rootClass} aria-label={t('b2bFlowGraphTitle')}>
      <div ref={containerRef} className={graphBoxClass}>
        <svg className={styles.flowSvg} viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet" overflow="visible">
          <defs>
            <radialGradient id={`${flowSvgUid}-flow-dot`} cx="40%" cy="40%" r="65%" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor="#fdf4ff" />
              <stop offset="60%" stopColor="#e879f9" />
              <stop offset="100%" stopColor="#a855f7" />
            </radialGradient>
          </defs>
          {/* Solar -> Hub edge */}
          <line
            x1={solarLine.start.x}
            y1={solarLine.start.y}
            x2={solarLine.end.x}
            y2={solarLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && solarW > 0 ? 'true' : 'false'}
          />
          {hasFlow && solarW > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={flowMotionPath(solarLine.start.x, solarLine.start.y, solarLine.end.x, solarLine.end.y)}
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* Grid <-> Hub: buying = grid->hub, selling = hub->grid */}
          <line
            x1={gridSelling ? gridLineSelling.start.x : gridLine.start.x}
            y1={gridSelling ? gridLineSelling.start.y : gridLine.start.y}
            x2={gridSelling ? gridLineSelling.end.x : gridLine.end.x}
            y2={gridSelling ? gridLineSelling.end.y : gridLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && Math.abs(gridW) > 0 ? 'true' : 'false'}
          />
          {hasFlow && Math.abs(gridW) > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={
                  gridSelling
                    ? flowMotionPath(
                        gridLineSelling.start.x,
                        gridLineSelling.start.y,
                        gridLineSelling.end.x,
                        gridLineSelling.end.y
                      )
                    : flowMotionPath(gridLine.start.x, gridLine.start.y, gridLine.end.x, gridLine.end.y)
                }
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* Wind -> Hub edge */}
          <line
            x1={windLine.start.x}
            y1={windLine.start.y}
            x2={windLine.end.x}
            y2={windLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && windW > 0 ? 'true' : 'false'}
          />
          {hasFlow && windW > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={flowMotionPath(windLine.start.x, windLine.start.y, windLine.end.x, windLine.end.y)}
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* ESS <-> Hub edge: charging = hub->ess, discharging = ess->hub */}
          <line
            x1={essCharging ? essLineCharging.start.x : essLine.start.x}
            y1={essCharging ? essLineCharging.start.y : essLine.start.y}
            x2={essCharging ? essLineCharging.end.x : essLine.end.x}
            y2={essCharging ? essLineCharging.end.y : essLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && Math.abs(essW) > 0 ? 'true' : 'false'}
          />
          {hasFlow && Math.abs(essW) > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={
                  essCharging
                    ? flowMotionPath(
                        essLineCharging.start.x,
                        essLineCharging.start.y,
                        essLineCharging.end.x,
                        essLineCharging.end.y
                      )
                    : flowMotionPath(essLine.start.x, essLine.start.y, essLine.end.x, essLine.end.y)
                }
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* Hub edge -> Miner (solar only) */}
          <line
            x1={minerLine.start.x}
            y1={minerLine.start.y}
            x2={minerLine.end.x}
            y2={minerLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && minerW > 0 ? 'true' : 'false'}
          />
          {hasFlow && minerW > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={flowMotionPath(minerLine.start.x, minerLine.start.y, minerLine.end.x, minerLine.end.y)}
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* Hub edge -> EV Charging */}
          <line
            x1={consumptionLine.start.x}
            y1={consumptionLine.start.y}
            x2={consumptionLine.end.x}
            y2={consumptionLine.end.y}
            className={styles.flowLineSvg}
            data-active={hasFlow && consumptionW > 0 ? 'true' : 'false'}
          />
          {hasFlow && consumptionW > 0 && (
            <circle r={flowDotR} fill={`url(#${flowSvgUid}-flow-dot)`} className={styles.flowDot}>
              <animateMotion
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;1"
                keySplines="0.45 0 0.55 1"
                path={flowMotionPath(
                  consumptionLine.start.x,
                  consumptionLine.start.y,
                  consumptionLine.end.x,
                  consumptionLine.end.y
                )}
              />
              <animate
                attributeName="opacity"
                values="0.98;0.98;0"
                keyTimes="0;0.82;1"
                dur={FLOW_DOT_MOTION_DUR}
                repeatCount="indefinite"
              />
            </circle>
          )}
        </svg>

        {/* Measured flow lines + dots for CSS grid mobile stack (embedded graph only) */}
        {useEmbeddedStackLayout && mg && mg.w > 0 && (
          <svg
            className={styles.flowSvgMobile}
            width="100%"
            height="100%"
            viewBox={`0 0 ${mg.w} ${mg.h}`}
            preserveAspectRatio="none"
            overflow="visible"
            aria-hidden
          >
            <defs>
              <radialGradient
                id={`${flowSvgUid}-mobile-flow-dot`}
                cx="40%"
                cy="40%"
                r="65%"
                gradientUnits="objectBoundingBox"
              >
                <stop offset="0%" stopColor="#fff0fb" />
                <stop offset="60%" stopColor="#ff65c8" />
                <stop offset="100%" stopColor="#f94caf" />
              </radialGradient>
            </defs>
            <line
              x1={mg.solar.start.x}
              y1={mg.solar.start.y}
              x2={mg.solar.end.x}
              y2={mg.solar.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && solarW > 0 ? 'true' : 'false'}
            />
            {hasFlow && solarW > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={flowMotionPath(mg.solar.start.x, mg.solar.start.y, mg.solar.end.x, mg.solar.end.y)}
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <line
              x1={gridSelling ? mg.gridSell.start.x : mg.gridBuy.start.x}
              y1={gridSelling ? mg.gridSell.start.y : mg.gridBuy.start.y}
              x2={gridSelling ? mg.gridSell.end.x : mg.gridBuy.end.x}
              y2={gridSelling ? mg.gridSell.end.y : mg.gridBuy.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && Math.abs(gridW) > 0 ? 'true' : 'false'}
            />
            {hasFlow && Math.abs(gridW) > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={
                    gridSelling
                      ? flowMotionPath(mg.gridSell.start.x, mg.gridSell.start.y, mg.gridSell.end.x, mg.gridSell.end.y)
                      : flowMotionPath(mg.gridBuy.start.x, mg.gridBuy.start.y, mg.gridBuy.end.x, mg.gridBuy.end.y)
                  }
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <line
              x1={mg.wind.start.x}
              y1={mg.wind.start.y}
              x2={mg.wind.end.x}
              y2={mg.wind.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && windW > 0 ? 'true' : 'false'}
            />
            {hasFlow && windW > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={flowMotionPath(mg.wind.start.x, mg.wind.start.y, mg.wind.end.x, mg.wind.end.y)}
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <line
              x1={essCharging ? mg.essCharge.start.x : mg.essDischarge.start.x}
              y1={essCharging ? mg.essCharge.start.y : mg.essDischarge.start.y}
              x2={essCharging ? mg.essCharge.end.x : mg.essDischarge.end.x}
              y2={essCharging ? mg.essCharge.end.y : mg.essDischarge.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && Math.abs(essW) > 0 ? 'true' : 'false'}
            />
            {hasFlow && Math.abs(essW) > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={
                    essCharging
                      ? flowMotionPath(
                          mg.essCharge.start.x,
                          mg.essCharge.start.y,
                          mg.essCharge.end.x,
                          mg.essCharge.end.y
                        )
                      : flowMotionPath(
                          mg.essDischarge.start.x,
                          mg.essDischarge.start.y,
                          mg.essDischarge.end.x,
                          mg.essDischarge.end.y
                        )
                  }
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <line
              x1={mg.miner.start.x}
              y1={mg.miner.start.y}
              x2={mg.miner.end.x}
              y2={mg.miner.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && minerW > 0 ? 'true' : 'false'}
            />
            {hasFlow && minerW > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={flowMotionPath(mg.miner.start.x, mg.miner.start.y, mg.miner.end.x, mg.miner.end.y)}
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <line
              x1={mg.consumption.start.x}
              y1={mg.consumption.start.y}
              x2={mg.consumption.end.x}
              y2={mg.consumption.end.y}
              className={styles.flowLineSvg}
              data-active={hasFlow && consumptionW > 0 ? 'true' : 'false'}
            />
            {hasFlow && consumptionW > 0 && (
              <circle r={flowDotMobileR} fill={`url(#${flowSvgUid}-mobile-flow-dot)`} className={styles.flowDot}>
                <animateMotion
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                  calcMode="spline"
                  keyTimes="0;1"
                  keySplines="0.45 0 0.55 1"
                  path={flowMotionPath(
                    mg.consumption.start.x,
                    mg.consumption.start.y,
                    mg.consumption.end.x,
                    mg.consumption.end.y
                  )}
                />
                <animate
                  attributeName="opacity"
                  values="0.98;0.98;0"
                  keyTimes="0;0.82;1"
                  dur={FLOW_DOT_MOTION_DUR}
                  repeatCount="indefinite"
                />
              </circle>
            )}
          </svg>
        )}

        {/* Solar - top left / left top in horizontal */}
        <div
          ref={setFlowNodeRef('solar')}
          className={styles.node}
          data-position={wideView ? 'left-top' : 'top-left'}
          data-flow-active={hasFlow && solarW > 0 ? 'true' : 'false'}
        >
          <div className={styles.nodeIcon}>
            <IoSunnyOutline size={kioskIconSize} />
          </div>
          <div className={styles.nodeLabel}>{t('b2bFlowGraphSolar')}</div>
          <NodePowerValue watts={solarW} className={styles.nodeValue} t={t} language={i18n.language} />
        </div>

        {/* Grid - top center / left center in horizontal (bidirectional: buy/sell); opens market-share chart */}
        <button
          type="button"
          ref={setFlowNodeRef('grid')}
          className={`${styles.node} ${styles.nodePressable}`}
          data-position={wideView ? 'left-center' : 'top-center'}
          data-flow-active={hasFlow && Math.abs(gridW) > 0 ? 'true' : 'false'}
          aria-haspopup="dialog"
          aria-expanded={marketChartOpen}
          aria-label={`${t('b2bFlowGraphGrid')} — ${t('b2bDayKwhChartTitle')}`}
          onClick={() => setMarketChartOpen(true)}
        >
          <div className={styles.nodeIcon}>
            <FaBolt size={kioskIconSize} />
          </div>
          <div className={styles.nodeLabel}>{t('b2bFlowGraphGrid')}</div>
          <NodePowerValue
            watts={gridSelling ? Math.abs(gridW) : gridW}
            prefix={gridSelling ? '↓' : undefined}
            className={styles.nodeValue}
            t={t}
            language={i18n.language}
          />
          {gridSelling && <span className={styles.essStatus}>{t('b2bFlowGraphGridSelling')}</span>}
        </button>

        {/* Wind - top right / left bottom in horizontal */}
        <button
          type="button"
          ref={setFlowNodeRef('wind')}
          className={`${styles.node} ${styles.nodePressable}`}
          data-position={wideView ? 'left-bottom' : 'top-right'}
          data-flow-active={hasFlow && windW > 0 ? 'true' : 'false'}
          aria-haspopup="dialog"
          aria-expanded={windShareOpen}
          aria-label={`${t('b2bFlowGraphWind')} — ${t('b2bFlowGraphWindShareAria')}`}
          onClick={() => setWindShareOpen(true)}
        >
          <div className={styles.nodeIcon}>
            <FaWind size={kioskIconSize} />
          </div>
          <div className={styles.nodeLabel}>{t('b2bFlowGraphWind')}</div>
          <NodePowerValue watts={windW} className={styles.nodeValue} t={t} language={i18n.language} />
        </button>

        {/* Central hub: whole card links to Open EMS */}
        <a
          ref={setFlowNodeRef('hub')}
          className={`${styles.hub} ${styles.hubLink}`}
          href={OPEN_EMS_UI_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-flow-active={hasFlow ? 'true' : 'false'}
          aria-label={t('b2bFlowGraphHubOpenEmsAria')}
        >
          <div className={styles.hubLogo} aria-hidden>
            <span className={styles.hubWordmark}>220-km.com</span>
            <SVGLogo
              width={wideView ? 87 : 76}
              height={wideView ? 48 : 41}
              className={styles.hubLogoSvg}
              focusable="false"
            />
          </div>
          <div className={styles.hubLabel}>
            {t('b2bFlowGraphEmsLinkText')}
            {t('b2bFlowGraphEmsVppSuffix')}
          </div>
        </a>

        {/* ESS - bottom left / right top in horizontal (bidirectional); opens vendor logos */}
        <button
          type="button"
          ref={setFlowNodeRef('ess')}
          className={`${styles.node} ${styles.nodePressable}`}
          data-position={wideView ? 'right-top' : 'bottom-left'}
          data-flow-active={hasFlow && Math.abs(essW) > 0 ? 'true' : 'false'}
          aria-haspopup="dialog"
          aria-expanded={essVendorsOpen}
          aria-label={`${t('b2bFlowGraphESS')} — ${t('b2bFlowGraphEssVendorsAria')}`}
          onClick={() => setEssVendorsOpen(true)}
        >
          <div className={styles.nodeIcon}>
            {essCharging ? (
              <BsBatteryCharging size={kioskIconSize} />
            ) : essDischarging ? (
              <BatteryTwoThirds size={kioskIconSize} />
            ) : (
              <BsBattery size={kioskIconSize} />
            )}
          </div>
          <div className={styles.nodeLabel}>{t('b2bFlowGraphESS')}</div>
          <NodePowerValue watts={Math.abs(essW)} className={styles.nodeValue} t={t} language={i18n.language} />
          {essCharging && <span className={styles.essStatus}>{t('b2bFlowGraphESSCharging')}</span>}
          {essDischarging && <span className={styles.essStatus}>{t('b2bFlowGraphESSDischarging')}</span>}
        </button>

        {/* ISIC Miner - bottom center / right center in horizontal (solar only, min 4%) */}
        <button
          type="button"
          ref={setFlowNodeRef('miner')}
          className={`${styles.node} ${styles.nodePressable}${minerW <= 0 ? ` ${styles.nodeMinerPowerIdle}` : ''}`}
          data-position={wideView ? 'right-center' : 'bottom-center'}
          data-flow-active={hasFlow && minerW > 0 ? 'true' : 'false'}
          aria-haspopup="dialog"
          aria-expanded={minerShareOpen}
          aria-label={
            minerSnap?.configured && minerSnap.workersActive != null && minerSnap.workersTotal != null
              ? `${t('b2bFlowGraphMiner')} (${minerSnap.workersActive}/${minerSnap.workersTotal}) — ${t('b2bFlowGraphMinerShareAria')}`
              : `${t('b2bFlowGraphMiner')} — ${t('b2bFlowGraphMinerShareAria')}`
          }
          onClick={() => setMinerShareOpen(true)}
        >
          <div className={styles.nodeIcon}>
            <FaMicrochip size={kioskIconSize} />
          </div>
          <div className={styles.nodeLabel}>
            {t('b2bFlowGraphMiner')}
            {minerSnap?.configured &&
              minerSnap.workersActive != null &&
              minerSnap.workersTotal != null &&
              ` (${minerSnap.workersActive}/${minerSnap.workersTotal})`}
          </div>
          <NodePowerValue watts={minerW} className={styles.nodeValue} t={t} language={i18n.language} />
          <div className={styles.minerInlineRow}>
            <div className={`${styles.nodeSubValue} ${styles.minerUsdtValue}`}>
              <span>{minedUsdtToday ?? '+xx'}</span>
              <FaBitcoin className={styles.minerUsdtIcon} aria-hidden />
            </div>
            <div className={styles.nodeMetaValue}>
              {minerTariffUahPerKwh ??
                `— ${
                  t('b2bMarketTariffUnitUahPerKwh').includes('/')
                    ? t('b2bMarketTariffUnitUahPerKwh')
                        .replace(/^[^/]+/, '\u20b4')
                        .replace(/кВт·год|кВт·ч|kWh/gi, 'кВтг')
                    : '\u20b4/кВтг'
                }`}
            </div>
          </div>
        </button>

        {/* EV Charging - bottom right / right bottom in horizontal */}
        <a
          ref={setFlowNodeRef('consumption')}
          href={EV_CHARGING_LIST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.node} ${styles.nodeExternalLink}`}
          data-position={wideView ? 'right-bottom' : 'bottom-right'}
          data-flow-active={hasFlow && consumptionW > 0 ? 'true' : 'false'}
          aria-label={`${t('b2bFlowGraphCharging')} — 220-km.com list`}
        >
          <div className={styles.nodeIcon}>
            <FaCar size={kioskIconSize} />
          </div>
          <div className={styles.nodeLabel}>{t('b2bFlowGraphCharging')}</div>
          <NodePowerValue
            watts={isLoading && realtimePower == null ? null : consumptionW}
            className={styles.nodeValue}
            t={t}
            language={i18n.language}
          />
          {(greenEnergyPct != null || greenEnergyPctLoading) && consumptionW > 0 ? (
            <div
              className={[
                styles.nodeGreenEnergy,
                greenEnergyPct != null && greenEnergyPct >= 70 ? styles.nodeGreenEnergyActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={styles.nodeGreenEnergyPct} style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}>
                {greenEnergyPctLoading && greenEnergyPct == null ? '…' : `${greenEnergyPct ?? '—'}%`}
              </span>
              <span className={styles.nodeGreenEnergyLabel}>{t('renewable')}</span>
            </div>
          ) : null}
        </a>
      </div>

      <Modal modalOpen={marketChartOpen} setModalOpen={setMarketChartOpen}>
        <div className={styles.marketShareModalBody}>
          <DayKwhChart embedded titleId={marketChartTitleId} />
        </div>
      </Modal>

      <Modal modalOpen={essVendorsOpen} setModalOpen={setEssVendorsOpen}>
        <div className={styles.essVendorsModalBody} aria-labelledby={essVendorsTitleId}>
          <h2 id={essVendorsTitleId} className={styles.essVendorsModalTitle}>
            {t('b2bFlowGraphEssVendorsTitle')}
          </h2>
          <div className={styles.essVendorsGrid}>
            {ESS_VENDOR_LOGOS.map(({ file, alt, href, certificateFile }) => {
              const logoSrc = `${publicUrl}/b2b-ess-vendors/${file}`;
              if (certificateFile) {
                return (
                  <button
                    key={file}
                    type="button"
                    className={styles.essVendorCard}
                    aria-label={t('b2bFlowGraphEssCertificateCardAria', { vendor: alt })}
                    onClick={() => setEssVendorCertificate({ file: certificateFile, href, vendorLabel: alt })}
                  >
                    <img className={styles.essVendorLogo} src={logoSrc} alt="" loading="lazy" decoding="async" />
                  </button>
                );
              }
              return (
                <button
                  key={file}
                  type="button"
                  className={styles.essVendorCard}
                  aria-label={t('b2bFlowGraphEssVendorShareCardAria', { vendor: alt })}
                  onClick={() => setEssVendorShare({ href, vendorLabel: alt })}
                >
                  <img className={styles.essVendorLogo} src={logoSrc} alt="" loading="lazy" decoding="async" />
                </button>
              );
            })}
          </div>
        </div>
      </Modal>

      <Modal
        modalOpen={essVendorShare != null}
        setModalOpen={open => {
          if (!open) setEssVendorShare(null);
        }}
      >
        <div className={styles.essVendorShareModal} role="dialog" aria-modal="true" aria-labelledby={essVendorShareTitleId}>
          <h2 id={essVendorShareTitleId} className={styles.essVendorShareTitle}>
            {essVendorShare
              ? t('b2bFlowGraphEssVendorShareTitle', { vendor: essVendorShare.vendorLabel })
              : t('b2bFlowGraphEssVendorShareTitleFallback')}
          </h2>
          <p className={styles.essVendorShareSubtitle}>{t('b2bFlowGraphEssVendorShareSubtitle')}</p>
          {essVendorShareQrUrl ? (
            <div className={styles.essVendorShareQrFrame}>
              <img
                src={essVendorShareQrUrl}
                alt={t('b2bFlowGraphEssVendorShareQrAlt', { vendor: essVendorShare?.vendorLabel ?? '' })}
                className={styles.essVendorShareQrImage}
              />
            </div>
          ) : (
            <div className={styles.essVendorShareQrPlaceholder} aria-hidden />
          )}
          <p className={styles.essVendorShareUrl}>{essVendorShare?.href}</p>
          <div className={styles.essVendorShareActions}>
            <button
              type="button"
              className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
              onClick={copyEssVendorShareLink}
            >
              {essVendorShareCopied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            </button>
            {essVendorShare?.href ? (
              <a
                href={essVendorShare.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
              >
                {t('b2bSectionShareOpenLink')}
              </a>
            ) : null}
          </div>
        </div>
      </Modal>

      <Modal
        modalOpen={essVendorCertificate != null}
        setModalOpen={open => {
          if (!open) setEssVendorCertificate(null);
        }}
      >
        <div className={styles.essCertificateModalBody} aria-labelledby={essVendorCertificateTitleId}>
          <h2 id={essVendorCertificateTitleId} className={styles.essCertificateModalTitle}>
            {t('b2bFlowGraphEssCertificateModalTitle')}
          </h2>
          {essVendorCertificate ? (
            <>
              <img
                className={styles.essCertificateImage}
                src={`${publicUrl}/b2b-ess-vendors/${essVendorCertificate.file}`}
                alt={t('b2bFlowGraphEssCertificateImageAlt', {
                  vendor: essVendorCertificate.vendorLabel,
                })}
                loading="eager"
                decoding="async"
              />
              <div className={styles.essCertificateFooter}>
                <a
                  href={essVendorCertificate.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.essCertificateSiteLink}
                  aria-label={`${certificateModalSiteDisplayName(essVendorCertificate.href)} — ${t('b2bFlowGraphEssVendorOpensExternal')}`}
                >
                  {certificateModalSiteDisplayName(essVendorCertificate.href)}
                </a>
              </div>
            </>
          ) : null}
        </div>
      </Modal>

      <Modal modalOpen={minerShareOpen} setModalOpen={setMinerShareOpen}>
        <div className={styles.essVendorShareModal} role="dialog" aria-modal="true" aria-labelledby={minerShareTitleId}>
          <h2 id={minerShareTitleId} className={styles.essVendorShareTitle}>
            {t('b2bFlowGraphMinerShareTitle')}
          </h2>
          <p className={styles.essVendorShareSubtitle}>{t('b2bFlowGraphMinerShareSubtitle')}</p>
          {minerShareQrUrl ? (
            <div className={styles.essVendorShareQrFrame}>
              <img
                src={minerShareQrUrl}
                alt={t('b2bFlowGraphMinerShareQrAlt')}
                className={styles.essVendorShareQrImage}
              />
            </div>
          ) : (
            <div className={styles.essVendorShareQrPlaceholder} aria-hidden />
          )}
          <p className={styles.essVendorShareUrl}>{BINANCE_MINER_OBSERVER_URL}</p>
          <div className={styles.essVendorShareActions}>
            <button
              type="button"
              className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
              onClick={copyMinerShareLink}
            >
              {minerShareCopied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            </button>
            <a
              href={BINANCE_MINER_OBSERVER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
            >
              {t('b2bSectionShareOpenLink')}
            </a>
          </div>
        </div>
      </Modal>

      <Modal modalOpen={windShareOpen} setModalOpen={setWindShareOpen}>
        <div className={styles.essVendorShareModal} role="dialog" aria-modal="true" aria-labelledby={windShareTitleId}>
          <h2 id={windShareTitleId} className={styles.essVendorShareTitle}>
            {t('b2bFlowGraphWindShareTitle')}
          </h2>
          <p className={styles.essVendorShareSubtitle}>{t('b2bFlowGraphWindShareSubtitle')}</p>
          {windShareQrUrl ? (
            <div className={styles.essVendorShareQrFrame}>
              <img
                src={windShareQrUrl}
                alt={t('b2bFlowGraphWindShareQrAlt')}
                className={styles.essVendorShareQrImage}
              />
            </div>
          ) : (
            <div className={styles.essVendorShareQrPlaceholder} aria-hidden />
          )}
          <p className={styles.essVendorShareUrl}>{WIND_NODE_DOC_URL}</p>
          <div className={styles.essVendorShareActions}>
            <button
              type="button"
              className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
              onClick={copyWindShareLink}
            >
              {windShareCopied ? t('b2bMarketplaceShareCopied') : t('b2bSectionShareCopyLink')}
            </button>
            <a
              href={WIND_NODE_DOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`b2b-btn b2b-btn--outline ${styles.essVendorShareActionBtn}`}
            >
              {t('b2bSectionShareOpenLink')}
            </a>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FlowGraph;
